import React from 'react';

const STATUS_LABEL = {
  ready: '已接入',
  partial: '部分数据',
  no_data: '待入库'
};

function SourceTag({ source }) {
  if (source === 'static_fallback') {
    return <span className="doc-source-tag fallback">静态回填</span>;
  }
  if (source === 'prospectus') {
    return <span className="doc-source-tag ok">说明书</span>;
  }
  return <span className="doc-source-tag missing">缺失</span>;
}

function formatNumber(value, digits = 2) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
}

export default function DocumentInsightPanel({ disclosure }) {
  if (!disclosure) {
    return <p className="decision-muted">暂无披露数据</p>;
  }

  const { prospectus, latest_report: report, recent_events: events, data_gaps: gaps } =
    disclosure;

  return (
    <div className="document-insight-panel">
      <div className="doc-insight-header">
        <span className={`doc-status status-${disclosure.status || 'no_data'}`}>
          {disclosure.status_label || STATUS_LABEL[disclosure.status] || '待入库'}
        </span>
        {prospectus ? (
          <span className="decision-muted">
            说明书 {prospectus.filled_count}/{prospectus.total_count} 项
          </span>
        ) : null}
      </div>

      <section className="doc-section">
        <h4 className="doc-section-title">说明书信息</h4>
        <ul className="doc-prospectus-list">
          {(prospectus?.items || []).map((item) => (
            <li className={`doc-prospectus-item ${item.value ? 'filled' : 'empty'}`} key={item.key}>
              <div className="doc-prospectus-label">
                {item.label}
                <SourceTag source={item.source} />
              </div>
              <div className="doc-prospectus-value">{item.value || '—'}</div>
            </li>
          ))}
        </ul>
      </section>

      <section className="doc-section">
        <h4 className="doc-section-title">最新运作报告</h4>
        {report ? (
          <>
            <div className="doc-report-meta">
              <span>报告期 {report.report_period}</span>
              {report.total_aum_text ? <span>规模 {report.total_aum_text}</span> : null}
              {report.leverage_ratio != null ? <span>杠杆率 {Number(report.leverage_ratio).toFixed(2)}%</span> : null}
              {report.duration_years != null ? <span>久期 {report.duration_years} 年</span> : null}
              {report.institutional_pct != null ? (
                <span>机构占比 {report.institutional_pct.toFixed(1)}%</span>
              ) : null}
            </div>
            {report.asset_allocation?.length ? (
              <div className="doc-allocation">
                <div className="doc-subtitle">实际资产配置</div>
                <div className="doc-allocation-bars">
                  {report.asset_allocation.filter((row) => Number(row.pct) > 0).map((row, idx) => (
                    <div className="doc-allocation-row" key={`${row.name || row.asset_class}-${idx}`}>
                      <span>{row.name || row.asset_class || '—'}</span>
                      <i><b style={{ width: `${Math.max(0, Math.min(100, Number(row.pct) || 0))}%` }} /></i>
                      <strong>{row.pct != null ? `${Number(row.pct).toFixed(2)}%` : '—'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {report.top_holdings?.length ? (
              <div className="doc-holdings">
                <div className="doc-subtitle">前十大持仓</div>
                <div className="doc-holdings-scroll">
                  <table className="doc-holdings-table">
                    <thead><tr><th>序号</th><th>资产名称</th><th>金额（万元）</th><th>占比</th><th>原文页</th></tr></thead>
                    <tbody>
                      {report.top_holdings.slice(0, 10).map((row, idx) => (
                        <tr key={`${row.name || row.bond_name}-${idx}`}>
                          <td>{row.rank || idx + 1}</td>
                          <td>{row.name || row.bond_name || '—'}</td>
                          <td>{formatNumber(row.amount_wan)}</td>
                          <td>{row.pct != null ? `${Number(row.pct).toFixed(2)}%` : '—'}</td>
                          <td>{row.page ? `第${row.page}页` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            <div className="doc-report-text-grid">
              {report.strategy_summary ? <div><div className="doc-subtitle">本期策略与操作</div><p className="doc-strategy">{report.strategy_summary}</p></div> : null}
              {report.performance_attribution?.outlook ? <div><div className="doc-subtitle">管理人后市展望</div><p className="doc-strategy">{report.performance_attribution.outlook}</p></div> : null}
            </div>
          </>
        ) : (
          <p className="decision-muted">暂无已发布运作报告；组合穿透与持仓信号暂不参与决策。</p>
        )}
      </section>

      {events?.length ? (
        <section className="doc-section">
          <h4 className="doc-section-title">近期披露事件</h4>
          <ul className="doc-event-list">
            {events.slice(0, 4).map((ev, idx) => (
              <li className={`doc-event severity-${ev.severity || 'info'}`} key={`${ev.event_date}-${idx}`}>
                <span className="doc-event-date">{ev.event_date || '—'}</span>
                <span className="doc-event-text">{ev.title || ev.summary || ev.event_type}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {gaps?.length ? (
        <section className="doc-section doc-gaps">
          <h4 className="doc-section-title">数据缺口</h4>
          <ul className="doc-gap-list">
            {gaps.slice(0, 8).map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {disclosure.disclaimer ? (
        <p className="decision-muted doc-disclaimer">{disclosure.disclaimer}</p>
      ) : null}
    </div>
  );
}
